import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Chip,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import { useQuery, useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { format } from 'date-fns';
import i18next from 'i18next';

import {
  LIST_BOOKING_COMMENTS_QUERY,
  DELETE_BOOKING_COMMENT_MUTATION,
  LIST_COMPANY_COMMENTS_FOR_BOOKING_QUERY,
} from '../../pages/bookings/gql';
import { dispatchMessage, dispatchException } from 'helper/snackbar';
import BookingCommentDialog from './BookingCommentDialog';
import ConfirmationButton from '../dialogs/ConfirmationButton';

interface BookingCommentsSectionProps {
  bookingId: number;
  companyId: number | null;
  clientUserId: number | null;
}

/**
 * Section component combining booking-specific comments and booking-relevant company comments.
 * Displays both types newest-first. Company comments are visually distinguished.
 * Only booking-relevant company comments matching the client are shown.
 */
const BookingCommentsSection: React.FC<BookingCommentsSectionProps> = ({ bookingId, companyId, clientUserId }) => {
  const dispatch = useDispatch();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingComment, setEditingComment] = useState<any>(null);

  // Fetch booking-specific comments
  const {
    data: bookingCommentsData,
    loading: bookingCommentsLoading,
    error: bookingCommentsError,
    refetch: refetchBookingComments
  } = useQuery(LIST_BOOKING_COMMENTS_QUERY, {
    variables: { bookingId },
    fetchPolicy: 'cache-and-network',
  });

  // Fetch booking-relevant company comments
  const {
    data: companyCommentsData,
    loading: companyCommentsLoading,
    error: companyCommentsError,
  } = useQuery(LIST_COMPANY_COMMENTS_FOR_BOOKING_QUERY, {
    variables: { companyId: companyId || 0, clientId: undefined },
    skip: !companyId,
    fetchPolicy: 'cache-and-network',
  });

  const [deleteCommentMutation] = useMutation(DELETE_BOOKING_COMMENT_MUTATION);

  const bookingComments = bookingCommentsData?.listBookingComments || [];
  const allCompanyComments = companyCommentsData?.listCompanyComments || [];

  const bookingRelevantCompanyComments = useMemo(() =>
    allCompanyComments
      .filter((c: any) =>
        c.isBookingRelevant &&
        (!c.publicClient?.id || c.publicClient.id === clientUserId)
      )
      .map((c: any) => ({ ...c, isCompanyComment: true })),
    [allCompanyComments, clientUserId]
  );

  const typedBookingComments = useMemo(() =>
    bookingComments.map((c: any) => ({ ...c, isCompanyComment: false })),
    [bookingComments]
  );

  const allComments = useMemo(() => {
    const combined = [...typedBookingComments, ...bookingRelevantCompanyComments];
    return combined.sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [typedBookingComments, bookingRelevantCompanyComments]);

  const handleAddComment = () => {
    setEditingComment(null);
    setOpenDialog(true);
  };

  const handleEditComment = (comment: any) => {
    setEditingComment(comment);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingComment(null);
  };

  const handleCommentSaved = () => {
    refetchBookingComments();
    handleCloseDialog();
  };

  const handleDeleteComment = async (id: number) => {
    try {
      await deleteCommentMutation({ variables: { id } });
      dispatchMessage(dispatch, String(i18next.t('booking-comment-delete-success')));
      refetchBookingComments();
    } catch (error: any) {
      dispatchException(dispatch, error);
    }
  };

  const loading = bookingCommentsLoading || companyCommentsLoading;
  const error = bookingCommentsError || companyCommentsError;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {i18next.t('booking-comments-error-loading')}
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" color="text.secondary">
            {allComments.length === 0
              ? i18next.t('booking-comment-no-comments')
              : allComments.length === 1
                ? i18next.t('comments-count-one', { count: allComments.length })
                : i18next.t('comments-count-many', { count: allComments.length })
            }
          </Typography>
          {bookingRelevantCompanyComments.length > 0 && (
            <Chip
              icon={<InfoIcon />}
              label={String(i18next.t('booking-comments-booking-relevant-count', { count: bookingRelevantCompanyComments.length }))}
              size="small"
              color="info"
              variant="outlined"
            />
          )}
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddComment}
        >
          {i18next.t('booking-comment-add')}
        </Button>
      </Box>

      {allComments.length === 0 ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          py={4}
          sx={{
            border: '2px dashed',
            borderColor: 'grey.300',
            borderRadius: 1,
            backgroundColor: 'grey.50'
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {i18next.t('booking-comment-no-comments')}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="50"></TableCell>
                <TableCell width="180">{i18next.t('booking-comment-created-at')}</TableCell>
                <TableCell width="200">{i18next.t('booking-comment-created-by')}</TableCell>
                <TableCell>{i18next.t('booking-comment-description')}</TableCell>
                <TableCell width="100" align="center">{i18next.t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allComments.map((comment: any) => (
                <TableRow
                  key={`${comment.isCompanyComment ? 'company' : 'booking'}-${comment.id}`}
                  hover
                  sx={comment.isCompanyComment ? {
                    backgroundColor: 'rgba(33, 150, 243, 0.08)'
                  } : undefined}
                >
                  <TableCell>
                    {comment.isCompanyComment && (
                      <Tooltip title={String(i18next.t('booking-comments-company-note-tooltip'))}>
                        <InfoIcon fontSize="small" color="info" />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {format(new Date(comment.createdAt), 'dd.MM.yyyy HH:mm')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {comment.createdBy?.name || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                      {comment.description}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {!comment.isCompanyComment && (
                      <Box display="flex" gap={0.5} justifyContent="center">
                        <Tooltip title={i18next.t('booking-comment-edit')}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditComment(comment)}
                            sx={{ color: 'primary.main' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <ConfirmationButton
                          confirmationTitle={i18next.t('booking-comment-delete')}
                          confirmationQuestion={i18next.t('booking-comment-delete-confirm')}
                          onConfirm={() => handleDeleteComment(comment.id)}
                          icon={true}
                          disabled={false}
                        >
                          <Tooltip title={i18next.t('booking-comment-delete')}>
                            <DeleteIcon fontSize="small" />
                          </Tooltip>
                        </ConfirmationButton>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <BookingCommentDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onSaved={handleCommentSaved}
        bookingId={bookingId}
        editingComment={editingComment}
      />
    </Box>
  );
};

export default BookingCommentsSection;